import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, MapPin, Clock, Building2 } from "lucide-react";
import CampaignHeader from "@/components/Frontend/components/CampaignHeader";
import CampaignHeadings from "@/components/Frontend/components/CampaignHeadings";
import CampaignBody from "@/components/Frontend/components/CampaignBody";
import { FrontendService } from "@/services/frontendService";
import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import Loader from "@/components/Loader";
import { GoogleFontLoader } from "@/components/GoogleFontLoader";
import { isLightColor } from "@/utils/helpers";
import CampaignPrePostText from "@/components/Frontend/components/CampaignPrePostText";

function Campaigns({ previewData = null, isPreview = false }) {
  const { companySlug, campaignSlug } = useParams();
  const [campaign, setCampaign] = useState({});
  const [campaign_headers, setCampignHeaders] = useState({
    name: "",
    logo: "",
  });
  const [campaign_header_sections, setCampignHeaderSections] = useState([{}]);
  const [campaign_body_sections, setCampignBodySections] = useState([{}]);
  const [company, setCompany] = useState({});
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    // If this is a preview, use the preview data instead of fetching
    if (isPreview && previewData) {
      const { formData, selectedCompany, micrositeData } = previewData;
      setCompany(selectedCompany);
      console.log("data", previewData);

      // Set campaign data from form
      setCampaign({
        title: formData.title,
        start_date: formData.startDate,
        start_time: formData.startTime,
        end_date: formData.endDate,
        end_time: formData.endTime,
        pre_campaign_text: formData.preCampaignText,
        end_campaign_text: formData.campaignEndMessage,
        pre_text_title: formData.preTextTitle,
        post_text_title: formData.postTextTitle,
        status: formData.status,
      });

      // Set campaign headers
      setCampignHeaders({
        name: formData.title,
        logo: selectedCompany?.logo?.original_url,
      });

      // Set header sections from microsite data
      setCampignHeaderSections(
        micrositeData?.headers?.map((header) => ({
          field_type: "heading",
          field_title: header.label,
          field_text: header.value,
        })) || [],
      );

      // Set body sections from microsite data
      setCampignBodySections(
        micrositeData?.bodySections?.map((section) => ({
          field_type: "body",
          field_title: section.name,
          field_text: section.content,
        })) || [],
      );

      // Set company data
      setCompany(selectedCompany);

      // Set mock vacancies for preview
      setVacancies([
        {
          id: 1,
          title: "Sample Vacancy 1",
          description:
            "<p>This is a sample vacancy description for preview purposes. It shows how the vacancy will appear on the actual campaign page.</p>",
          status: "1",
          vacancy_tag: [{ title: "Full-time" }, { title: "Remote" }],
          slug: "sample-vacancy-1",
        },
        {
          id: 2,
          title: "Sample Vacancy 2",
          description:
            "<p>Another sample vacancy to demonstrate how multiple vacancies will be displayed in the campaign preview.</p>",
          status: "1",
          vacancy_tag: [{ title: "Part-time" }, { title: "On-site" }],
          slug: "sample-vacancy-2",
        },
      ]);

      setLoading(false);
      return;
    }

    // Original API call logic for non-preview mode
    const fetchData = async () => {
      try {
        const _campaign = await FrontendService.getCampaignBySlug(
          companySlug,
          campaignSlug,
        );
        if (_campaign) {
          setCampaign(_campaign);
          setCampignHeaders({
            name: _campaign.title,
            logo: _campaign.company?.logo?.url,
          });
          setCampignHeaderSections(
            _campaign?.campaign_fields?.filter(
              (item) => item.field_type == "heading",
            ),
          );
          setCampignBodySections(
            _campaign?.campaign_fields?.filter(
              (item) => item.field_type == "body",
            ),
          );
          setVacancies(_campaign?.vacancies);
          setCompany(_campaign?.company);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching campaign:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [companySlug, campaignSlug, isPreview, previewData]);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );

  const checkCampaign = (OnlyCheckCondition = false) => {
    const currentDate = new Date();
    const startDate = new Date(
      campaign?.start_date + " " + campaign?.start_time,
    );
    const endDate = new Date(campaign?.end_date + " " + campaign?.end_time);
    if (currentDate < startDate) {
      if (OnlyCheckCondition) {
        return true;
      }
      return <CampaignPrePostText campaign={campaign} isPreCampaign={true} />;
    }
    if (currentDate > endDate) {
      if (OnlyCheckCondition) {
        return true;
      }
      return <CampaignPrePostText campaign={campaign} isEndCampaign={true} />;
    }
    return null;
  };

  // Add the GoogleFontLoader component
  return (
    <>
      {company?.brand_font && (
        <GoogleFontLoader key={company?.brand_font} font={company.brand_font} />
      )}
      <div
        className="min-h-screen bg-muted p-4 list-style-initial"
        style={{ fontFamily: company?.brand_font || "inherit" }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Logo */}
          <CampaignHeader
            campaign={campaign_headers}
            brand_color={company?.brand_color}
          />
          {checkCampaign()}

          <CampaignHeadings
            campaignheadings={campaign_header_sections}
            company={company}
            text_class={
              company?.brand_color && isLightColor(company.brand_color)
                ? "text-foreground"
                : "text-white"
            }
          />

          <CampaignBody campaignbody={campaign_body_sections} />

          {checkCampaign(true) ?? (
            <div
              className={`grid gap-6 mb-8 ${
                (vacancies?.filter((vacancy) => vacancy.status == "1")
                  ?.length || 0) === 1
                  ? "grid-cols-1"
                  : "grid-cols-1 md:grid-cols-2"
              }`}
            >              {vacancies
                ?.filter((vacancy) => vacancy.status == "1")
                ?.map((vacancy) => (
                  <div
                    key={vacancy.id}
                    className="bg-card rounded-xl shadow-lg overflow-hidden animate-pulse"
                  >
                    <div className="p-6 bg-muted">
                      <div className="h-8 bg-input rounded w-3/4 mb-4"></div>
                      <div className="flex flex-wrap gap-4">
                        {[1, 2, 3].map((_, index) => (
                          <div
                            key={index}
                            className="bg-input px-3 py-1 rounded-lg flex items-center gap-2"
                          >
                            <div className="h-4 w-16"></div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        <div className="h-4 bg-muted rounded"></div>
                        <div className="h-4 bg-muted rounded w-5/6"></div>
                        <div className="h-4 bg-muted rounded w-2/3"></div>
                        <div className="h-4 bg-muted rounded w-3/4"></div>
                      </div>
                      <div className="mt-4">
                        <div className="h-12 bg-input rounded-lg w-32"></div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Campaigns;
